import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { EllipsisHorizontalIcon } from "@heroicons/react/20/solid";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";

import type { SnackbarData } from "../../components/Snackbar";
import AuditHistoriesLink from "../../components/audit/AuditHistoriesLink";
import { buildAuditUrl } from "../../components/audit/auditEntityConfig";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import EmptyModal from "../../components/modals/EmptyModal";
import OperationalDataTableSkeleton from "../../components/operational/OperationalDataTableSkeleton";
import SlideOver from "../../components/slideOver/SlideOver";
import type { UserRecord } from "../../features/users/userTypes";
import { withRouter } from "../../shared/utility";
import * as actions from "../../store/actions/index";
import { UsersViewModel } from "../../viewmodels/setup/users/UsersViewModel";

interface UsersProps {
  addSnackbar: (snackbar: SnackbarData) => void;
  logout: () => void;
}

function Users({ addSnackbar, logout }: UsersProps) {
  const {
    openAddUserSlideOver,
    getUsers,
    loading,
    users,
    setSlideOver,
    slideOver,
    viewUserDetails,
    deleteUser,
    emptyModal,
    setEmptyModal,
    openChangePasswordModal,
  } = UsersViewModel(logout, addSnackbar);

  const currentPage = 1;
  const searchRef = useRef<HTMLInputElement>(null);
  const columns = [
    "Name",
    "Username",
    "Email",
    "Type",
    "Department",
    "Employee type",
    "Actions",
  ];

  useEffect(() => {
    void getUsers(null, currentPage);
    // getUsers is owned by this screen model and intentionally runs on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    void getUsers(searchRef.current?.value ?? "", currentPage);
  };

  const askDeleteUser = (user: UserRecord) => {
    setEmptyModal({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => {
            setEmptyModal({ childComponent: null, open: false });
            void deleteUser(user);
          }}
          message={`Are you sure you want to delete ${user.name}?`}
          setOpen={setEmptyModal}
          title="Delete user"
        />
      ),
      open: true,
    });
  };

  return (
    <div>
      <div className="mt-2">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="card-custom px-4 py-5 sm:flex sm:items-center sm:px-4">
            <div className="sm:flex-auto">
              <h1 className="text-base font-semibold leading-6 text-gray-900">
                Users
              </h1>
              <div className="mt-5 flex rounded-md">
                <div className="relative flex flex-grow items-stretch focus-within:z-10">
                  <input
                    type="search"
                    name="searchUser"
                    id="searchUser"
                    className="block w-full rounded-lg rounded-br-none rounded-tr-none border border-gray-300 pl-2 sm:text-sm"
                    placeholder="Search by name, username, or email"
                    ref={searchRef}
                  />
                </div>
                <button
                  type="button"
                  className="relative -ml-px inline-flex items-center space-x-2 rounded-r-md border border-gray-300 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                  onClick={handleSearch}
                >
                  <MagnifyingGlassIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </button>
                <button
                  type="button"
                  className="ml-5 rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600"
                  onClick={openAddUserSlideOver}
                >
                  Add user
                </button>
                <AuditHistoriesLink slug="user" className="ml-5" />
              </div>
            </div>
          </div>
          <div className="mt-8 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                {loading ? (
                  <OperationalDataTableSkeleton columns={columns} rows={8} />
                ) : (
                  <div className="card-custom overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-300">
                      <thead className="bg-white">
                        <tr>
                          {columns.map((heading) => (
                            <th
                              key={heading}
                              scope="col"
                              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 first:pl-6 last:text-right"
                            >
                              {heading}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {users.data.map((user) => (
                          <tr key={user.id}>
                            <td className="whitespace-nowrap py-1.5 pl-6 pr-3 text-sm font-medium text-gray-900">
                              {user.name}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-700">
                              {user.username}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {user.email}
                              {user.email_verified_at === null ? (
                                <p className="font-bold text-red-500">
                                  Email not verified
                                </p>
                              ) : null}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {user.user_type.label}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {(user.department ?? [])
                                .map((department) => department.label)
                                .join(", ")}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {user.employee_type?.label ?? ""}
                            </td>
                            <td className="relative whitespace-nowrap py-1.5 pl-3 pr-6 text-right text-sm font-medium">
                              <div className="inline-flex items-center justify-end gap-2">
                                <button
                                  onClick={() => viewUserDetails(user)}
                                  className="rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600"
                                >
                                  Edit
                                </button>
                                <Menu>
                                  <MenuButton
                                    className="inline-flex items-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                                    aria-label={`Actions for ${user.name}`}
                                  >
                                    <EllipsisHorizontalIcon
                                      className="h-5 w-5"
                                      aria-hidden="true"
                                    />
                                  </MenuButton>
                                  <MenuItems
                                    anchor="bottom end"
                                    className="z-20 w-44 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-gray-900/5 [--anchor-gap:4px] focus:outline-none"
                                  >
                                    <MenuItem>
                                      <button
                                        type="button"
                                        className="block w-full px-3 py-1.5 text-left text-sm text-gray-900 data-[focus]:bg-gray-50"
                                        onClick={() =>
                                          openChangePasswordModal(user)
                                        }
                                      >
                                        Change password
                                      </button>
                                    </MenuItem>
                                    <MenuItem>
                                      <a
                                        href={buildAuditUrl(
                                          "user",
                                          user.id,
                                          user.name,
                                        )}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block px-3 py-1.5 text-sm text-gray-900 data-[focus]:bg-gray-50"
                                      >
                                        Audit
                                      </a>
                                    </MenuItem>
                                    <MenuItem>
                                      <button
                                        type="button"
                                        className="block w-full px-3 py-1.5 text-left text-sm text-red-600 data-[focus]:bg-gray-50"
                                        onClick={() => askDeleteUser(user)}
                                      >
                                        Delete
                                      </button>
                                    </MenuItem>
                                  </MenuItems>
                                </Menu>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModal}
      />
      <SlideOver
        childComponent={slideOver.childComponent}
        customClass="max-w-3xl"
        slideOver={slideOver}
        setSlideOver={setSlideOver}
      />
    </div>
  );
}

const mapDispatchToProps = (dispatch: any) => ({
  logout: () => dispatch(actions.logout()),
  addSnackbar: (snackbar: SnackbarData) =>
    dispatch(actions.addSnackbar(snackbar)),
});

export default withRouter(connect(null, mapDispatchToProps)(Users));
